import { useTranslation } from 'react-i18next'
import { Box, Button, Checkbox, Grid, Skeleton, Text } from '@chakra-ui/react'
import { AppCard } from '@/shared'
import type { IBloodCollectionAnalysis } from '../model/blood-collection.type'

interface BloodCollectionAnalysesCardProps {
  analyses: IBloodCollectionAnalysis[]
  selectedAnalysisIds: Set<string>
  onAnalysisToggle: (analysisId: string, checked: boolean) => void
  disabled?: boolean
  isLoading?: boolean
  analysisLabelKey?: 'analysis_name_ru' | 'analysis_name_en'
  hasChanges?: boolean
  isSaving?: boolean
  onSave?: () => void
}

export function BloodCollectionAnalysesCard({
  analyses,
  selectedAnalysisIds,
  onAnalysisToggle,
  disabled = false,
  isLoading = false,
  analysisLabelKey = 'analysis_name_ru',
  hasChanges = false,
  isSaving = false,
  onSave,
}: BloodCollectionAnalysesCardProps) {
  const { t } = useTranslation()

  return (
    <AppCard.Root>
      <AppCard.Body p={5}>
        <Text fontWeight="semibold" fontSize="md" mb={4}>
          {t('laboratory_research.blood_collection.analyses_title')}
        </Text>

        {isLoading ? (
          <Skeleton h="120px" />
        ) : (
          <Box bg="bg.subtle" borderRadius="lg" p={4}>
            <Grid
              templateColumns={{
                base: '1fr',
                md: 'repeat(2, 1fr)',
                xl: 'repeat(3, 1fr)',
              }}
              gap={3}
            >
              {analyses?.length > 0 &&
                analyses.map((analysis) => {
                  const checked = selectedAnalysisIds.has(analysis.id)
                  return (
                    <Checkbox.Root
                      key={analysis.id}
                      checked={checked}
                      disabled={disabled}
                      colorPalette="red"
                      onCheckedChange={(e) =>
                        onAnalysisToggle(analysis.id, !!e.checked)
                      }
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control cursor="pointer" />
                      <Checkbox.Label fontSize="sm">
                        {analysis[analysisLabelKey]}
                      </Checkbox.Label>
                    </Checkbox.Root>
                  )
                })}
            </Grid>
          </Box>
        )}

        {hasChanges && (
          <Box mt={4} display="flex" justifyContent="flex-end">
            <Button
              bg="primary.solid"
              color="white"
              borderRadius="md"
              size="sm"
              px={6}
              disabled={isSaving}
              loading={isSaving}
              onClick={onSave}
              _hover={{ opacity: 0.85 }}
              _active={{ opacity: 0.7 }}
            >
              {t('laboratory_research.blood_collection.save')}
            </Button>
          </Box>
        )}
      </AppCard.Body>
    </AppCard.Root>
  )
}
